import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, X, Check, Mail, Phone, MapPin } from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentProfileSafe, getCurrentUserId, supabase, Owner } from '../lib/supabase';
import AuditService from '../services/AuditService';
import toast from 'react-hot-toast';

const Owners: React.FC = () => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [ownerRole, setOwnerRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    telephone: '',
    postal_address: '',
    is_active: true,
  });

  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const profile = await getCurrentProfileSafe();
      setOwnerRole(profile?.role || 'staff');

      const id = await getCurrentBodyCorporateId();
      setBodyCorporateId(id);
      await fetchOwners(id);
    };

    load();
  }, []);

  const fetchOwners = async (id: string | null) => {
    setLoading(true);

    if (!id) {
      setOwners([]);
      setLoading(false);
      toast.error('No body corporate is configured for this user.');
      return;
    }

    const { data, error } = await supabase
      .from('owners')
      .select('*')
      .eq('body_corporate_id', id)
      .order('full_name');

    if (error) {
      toast.error('Error fetching owners');
      console.error(error);
    } else {
      setOwners(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (ownerRole === 'viewer') {
      toast.error('Viewer accounts can only view owners.');
      return;
    }

    if (editingOwner) {
      const changes = { full_name: formData.full_name, email: formData.email, telephone: formData.telephone, postal_address: formData.postal_address, is_active: formData.is_active };
      const { error } = await supabase
        .from('owners')
        .update(changes)
        .eq('id', editingOwner.id);

      if (error) {
        toast.error('Error updating owner');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'owner', editingOwner.id, editingOwner, { ...editingOwner, ...changes });
        toast.success('Owner updated successfully');
        fetchOwners(bodyCorporateId);
        closeModal();
      }
    } else {
      const { data: createdOwner, error } = await supabase
        .from('owners')
        .insert({
          body_corporate_id: bodyCorporateId,
          full_name: formData.full_name,
          email: formData.email,
          telephone: formData.telephone,
          postal_address: formData.postal_address,
          is_active: formData.is_active,
        }).select().single();

      if (error) {
        toast.error('Error creating owner');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId && createdOwner) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'owner', createdOwner.id, undefined, createdOwner);
        toast.success('Owner created successfully');
        fetchOwners(bodyCorporateId);
        closeModal();
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this owner? This may affect linked accounts.')) return;

    const deletedOwner = owners.find((owner) => owner.id === id);
    const { error } = await supabase
      .from('owners')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error deleting owner');
      console.error(error);
    } else {
      const userId = await getCurrentUserId();
      if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('DELETE', 'owner', id, deletedOwner);
      toast.success('Owner deleted successfully');
      fetchOwners(bodyCorporateId);
    }
  };

  const openModal = (owner?: Owner) => {
    if (owner) {
      setEditingOwner(owner);
      setFormData({
        full_name: owner.full_name,
        email: owner.email || '',
        telephone: owner.telephone || '',
        postal_address: owner.postal_address || '',
        is_active: owner.is_active,
      });
    } else {
      setEditingOwner(null);
      setFormData({
        full_name: '',
        email: '',
        telephone: '',
        postal_address: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOwner(null);
  };

  const filteredOwners = owners.filter(owner =>
    owner.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    owner.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Owners</h1>
          <p className="text-gray-500">Manage all property owners</p>
        </div>
        {ownerRole !== 'viewer' && (
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 btn-primary"
          >
            <Plus size={20} />
            Add Owner
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filteredOwners.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No owners found. Click "Add Owner" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOwners.map((owner) => (
                  <tr key={owner.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{owner.full_name}</div>
                      {owner.postal_address && (
                        <div className="text-xs text-gray-500">{owner.postal_address}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{owner.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{owner.telephone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        owner.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {owner.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {ownerRole !== 'viewer' && (
                        <>
                          <button
                            onClick={() => openModal(owner)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(owner.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {editingOwner ? 'Edit Owner' : 'Add New Owner'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telephone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      placeholder="+27 82 123 4567"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Address
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
                    <textarea
                      value={formData.postal_address}
                      onChange={(e) => setFormData({ ...formData, postal_address: e.target.value })}
                      placeholder="PO Box 123, City"
                      rows={2}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                >
                  <Check size={20} />
                  {editingOwner ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Owners;
